import { Component, createRef, cloneElement, FunctionComponent } from 'react';
import styles from './styles.scss';
import { Block } from 'components/Shared/Layout';
import cn from 'classnames';
import {
  getPosition,
  SwipeDirection,
  getSwipeDirection,
  calcMoveTranslateValue,
  Point,
  getContainerStyles,
  isSwipe,
  isScroll,
  SwipeDirectionArgs
} from './utils';
import { PrevNextControls } from './control-button';
import warning from 'warning';
import { getQaDataAttr } from '@bygghemmait/utilities';

interface CarouselState {
  activeSlideIndex: number;
  translateValue: number;
  shouldAnimate: boolean;
}

interface CarouselClassNames {
  controls?: string;
  tabs?: string;
  slides?: string;
}
export type RenderSlideTabComponent<T = unknown> = FunctionComponent<{
  slide?: T;
  goToSlide?: () => void;
  isActiveSlide?: () => boolean;
  isAdjacentSlide?: () => boolean;
}>;
interface CarouselProps<T = unknown> {
  slidesData: T[];
  children: (slide: T, i: number, active?: boolean) => JSX.Element;
  autoplay?: boolean;
  interval?: number;
  displayControls?: boolean;
  RenderSlideTab?: RenderSlideTabComponent<T>;
  onChange?: (slide: T) => void;
  defaultFocus?: boolean;
  className?: string;
  classNames?: CarouselClassNames;
  startAtIndex?: number;
  autoFocus?: boolean;
  spacing?: boolean;
  onStartPage?: boolean;
}

const getInitialState = (startIndex: number): CarouselState => ({
  translateValue: HUNDRED * (startIndex + 1),
  shouldAnimate: true,
  activeSlideIndex: startIndex
});

const HUNDRED = 100;
const DEFAULT_INTERVAL = 5000;
const SCROLL_LISTENER_OPTIONS = { passive: false };

let didWarnAboutStartIndex = false;

export default class Carousel<T = never> extends Component<CarouselProps<T>, CarouselState> {
  private intervalHandle: number | undefined;
  private timeoutHandle: number | undefined;
  private initialTouchPosition: Point;
  private previousTouchPosition: Point;
  private currentTouchPosition: Point;
  private clickDisabled = false;
  private isAnimating = false;
  private isDragging = false;
  private swipeDisabled = false;
  private isScroll = false;
  private isSwipe = false;
  private ref = createRef<HTMLDivElement>();

  constructor(props: CarouselProps<T>) {
    super(props);
    let startIndex = 0;
    if (props.startAtIndex !== undefined) {
      const validStartIndex =
        props.startAtIndex >= 0 && props.startAtIndex < props.slidesData.length;

      if (!didWarnAboutStartIndex) {
        warning(
          validStartIndex,

          `Carousel expected startindex to be 0 - ${props.slidesData.length - 1}, got ${
            props.startAtIndex
          }. Will default to 0.`
        );
        didWarnAboutStartIndex = true;
      }
      startIndex = validStartIndex ? props.startAtIndex : 0;
    }
    this.state = getInitialState(startIndex);
  }

  public componentDidMount() {
    this.props.autoplay && this.startAutoplay();
    if (!this.isSingleSlide()) {
      document.addEventListener('scroll', this.preventScrollWhileSwiping, SCROLL_LISTENER_OPTIONS);

      document.addEventListener(
        'touchmove',
        this.preventScrollWhileSwiping,
        SCROLL_LISTENER_OPTIONS
      );
    }

    if (this.props.autoFocus && this.ref.current) {
      this.ref.current.focus();
    }
  }

  public componentWillUnmount() {
    this.intervalHandle && clearInterval(this.intervalHandle);
    if (!this.isSingleSlide()) {
      document.removeEventListener('scroll', this.preventScrollWhileSwiping);
      document.removeEventListener('touchmove', this.preventScrollWhileSwiping);
    }
  }

  public render() {
    const { translateValue, shouldAnimate } = this.state;
    const {
      children,
      displayControls,
      RenderSlideTab,
      className,
      slidesData: slides,
      classNames = {},
      spacing,
      onStartPage
    } = this.props;
    const last = slides[slides.length - 1];
    const first = slides[0];

    return (
      <div ref={this.ref} tabIndex={0} className={className} onKeyDown={this.handleKeyDown}>
        <Block
          className={cn(styles.carousel, classNames.slides)}
          onMouseOver={this.pauseAutoplay}
          onMouseOut={this.startAutoplay}
        >
          <div
            className={styles.slider}
            onTransitionEnd={this.onTransitionEnd}
            {...getQaDataAttr({ id: 'carousel-animator' })}
            style={this.isSingleSlide() ? {} : getContainerStyles(translateValue, shouldAnimate)}
          >
            {!this.isSingleSlide() && (
              <div
                aria-hidden="true"
                className={cn(styles.slideContainer, { [styles.spacing]: spacing })}
              >
                {cloneElement(children(last, -1))}
              </div>
            )}
            {slides.map((slide, i) => (
              <div
                className={cn(styles.slideContainer, { [styles.spacing]: spacing })}
                key={i}
                aria-hidden={!this.isActiveSlide(i)()}
                {...this.getTouchEvents()}
                {...this.getMouseEvents()}
              >
                {cloneElement<React.HTMLAttributes<HTMLElement>>(
                  children(slide, i, this.isActiveSlide(i)())
                )}
              </div>
            ))}
            {!this.isSingleSlide() && (
              <div
                aria-hidden="true"
                className={cn(styles.slideContainer, { [styles.spacing]: spacing })}
              >
                {cloneElement(children(first, -1))}
              </div>
            )}
          </div>
        </Block>
        {displayControls && slides.length > 1 && (
          <PrevNextControls
            className={cn(styles.controls, classNames.controls)}
            onPrevious={this.previousSlide}
            onNext={this.nextSlide}
          />
        )}
        {RenderSlideTab && slides.length > 1 && (
          <div
            className={cn(styles.slideTabsWrapper, classNames.tabs, {
              [styles.noMargin]: onStartPage
            })}
          >
            {slides.map((slide, i) => (
              <RenderSlideTab
                key={i}
                slide={slide}
                goToSlide={this.goToSlide(i)}
                isActiveSlide={this.isActiveSlide(i)}
                isAdjacentSlide={this.isAdjacentSlide(i)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  private isActiveSlide = (index: number) => () => index === this.state.activeSlideIndex;
  private isAdjacentSlide = (index: number) => () => {
    return index === this.state.activeSlideIndex - 1 || index === this.state.activeSlideIndex + 1;
  };
  private isSingleSlide = () => this.props.slidesData.length === 1;
  private canSlide = (index: number) => !this.isAnimating && this.state.activeSlideIndex !== index;
  private isLastSlide = () => this.state.activeSlideIndex === this.props.slidesData.length - 1;
  private isFirstSlide = () => this.state.activeSlideIndex === 0;

  private goToSlide = (index: number, translateValue?: number) => () => {
    const newTranslateValue = translateValue !== undefined ? translateValue : HUNDRED * (index + 1);
    if (this.canSlide(index)) {
      this.setState({
        activeSlideIndex: index,
        shouldAnimate: true,
        translateValue: newTranslateValue
      });
      this.swipeDisabled = true;
      this.isAnimating = true;
      this.isDragging = false;

      this.props.onChange && this.props.onChange(this.props.slidesData[index]);
    }
  };

  private nextSlide = () => {
    const { activeSlideIndex } = this.state;
    const { slidesData } = this.props;

    const index = (activeSlideIndex + 1) % slidesData.length;
    const translateValue = this.isLastSlide()
      ? HUNDRED * (slidesData.length + 1)
      : HUNDRED * (index + 1);

    this.goToSlide(index, translateValue)();
  };

  private previousSlide = () => {
    const { activeSlideIndex } = this.state;
    const { slidesData } = this.props;

    const index = (activeSlideIndex - 1 + slidesData.length) % slidesData.length;
    const translateValue = this.isFirstSlide() ? 0 : HUNDRED * (index + 1);

    this.goToSlide(index, translateValue)();
  };

  private resetSlide = (direction: SwipeDirection) => {
    this.isDragging = false;
    this.isAnimating = false;
    this.swipeDisabled = direction !== SwipeDirection.Click;
    this.setState({
      translateValue: Math.round(this.state.translateValue / HUNDRED) * HUNDRED,
      shouldAnimate: true
    });
  };

  private onTransitionEnd = () => {
    const { slidesData } = this.props;

    this.isAnimating = false;
    this.swipeDisabled = false;

    if (this.isFirstSlide() || this.isLastSlide()) {
      const newTranslateValue = this.isLastSlide() ? slidesData.length * HUNDRED : HUNDRED;

      this.setState({
        translateValue: newTranslateValue,
        shouldAnimate: false
      });
    }
  };

  private pauseAutoplay = () =>
    this.props.autoplay && this.intervalHandle && clearInterval(this.intervalHandle);

  private startAutoplay = () => {
    if (this.props.autoplay && !this.isSingleSlide()) {
      const interval = this.props.interval || DEFAULT_INTERVAL;
      this.pauseAutoplay();
      this.intervalHandle = window.setInterval(this.nextSlide, interval);
      this.isDragging = false;
      this.timeoutHandle && window.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }
  };

  private moveStart = (pos: Point) => {
    if (this.swipeDisabled) {
      return;
    }
    this.initialTouchPosition = { ...pos };
    this.currentTouchPosition = { ...pos };
    this.previousTouchPosition = { ...pos };
    this.isDragging = true;
    this.setState({ shouldAnimate: false });
  };

  private move = ({ x, y }: Point) => {
    if (this.swipeDisabled || this.isScroll) {
      return;
    }

    this.currentTouchPosition = { x, y };
    const carouselWidth = (this.ref.current && this.ref.current.clientWidth) || 0;

    const newTranslateValue = calcMoveTranslateValue({
      carouselWidth,
      currentTranslateValue: this.state.translateValue,
      currentPosition: this.currentTouchPosition,
      previousPosition: this.previousTouchPosition
    });

    const args: SwipeDirectionArgs = {
      initial: this.initialTouchPosition,
      current: this.currentTouchPosition
    };

    if (!this.isScroll && isSwipe(args)) {
      this.isSwipe = true;
    }
    if (!this.isSwipe && isScroll(args)) {
      this.isScroll = true;
    }

    this.setState({ translateValue: newTranslateValue });
    this.previousTouchPosition = { ...this.currentTouchPosition };
  };

  private moveEnd = () => {
    if (this.swipeDisabled) {
      return;
    }

    this.isSwipe = false;
    this.isScroll = false;

    const direction = getSwipeDirection({
      initial: this.initialTouchPosition,
      current: this.currentTouchPosition
    });

    this.initialTouchPosition = { x: 0, y: 0 };
    this.currentTouchPosition = { x: 0, y: 0 };
    this.previousTouchPosition = { x: 0, y: 0 };

    this.clickDisabled = direction !== SwipeDirection.Click;

    if (direction === SwipeDirection.Right) {
      this.previousSlide();
    } else if (direction === SwipeDirection.Left) {
      this.nextSlide();
    } else {
      this.resetSlide(direction);
    }

    this.clickDisabled = direction !== SwipeDirection.Click;
    this.timeoutHandle && window.clearTimeout(this.timeoutHandle);
    this.timeoutHandle = window.setTimeout(
      this.startAutoplay,
      this.props.interval || DEFAULT_INTERVAL
    );
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      this.nextSlide();
    }
    if (e.key === 'ArrowLeft') {
      this.previousSlide();
    }
  };

  private preventScrollWhileSwiping: EventListener = e => {
    if (this.isSwipe && e.cancelable) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };

  private getMouseEvents = () => ({
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => this.moveStart(getPosition(e)),
    onMouseMove: (e: React.MouseEvent<HTMLDivElement>) =>
      this.isDragging && this.move(getPosition(e)),
    onMouseUp: () => this.moveEnd(),
    onMouseOut: () => {
      if (this.isDragging) {
        this.moveEnd();
      }
    },
    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
      if (this.clickDisabled) {
        e.preventDefault();
        this.clickDisabled = false;
      }
    }
  });

  private getTouchEvents = () => ({
    onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => {
      this.pauseAutoplay();
      this.moveStart(getPosition(e));
    },
    onTouchMove: (e: React.TouchEvent<HTMLDivElement>) => this.move(getPosition(e)),
    onTouchEnd: () => this.moveEnd(),
    onDragStart: (e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()
  });
}
